import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "../../theme/ThemeProvider";
import Layout from "../../components/Layout";
import { useSEO } from "../../hooks/useSEO";
import { BUY_ONLINE_ROUTE } from "../../routes/constant";
import {
  submitContactForm,
  resetContactState,
} from "../../store/slices/contactSlice";
import {
  FiShoppingBag,
  FiUser,
  FiMail,
  FiPhone,
  FiMessageSquare,
  FiAlertCircle,
  FiShieldOff,
  FiSend,
  FiCheckCircle,
} from "react-icons/fi";
import toast from "react-hot-toast";
import RectangleGridButtons from "../../components/home/nav/NavButton";

const BuyOnline = () => {
  const { isDarkMode } = useTheme();
  const dispatch = useDispatch();
  const [captchaValue, setCaptchaValue] = useState("");
  const [captchaError, setCaptchaError] = useState("");

  // Generate random CAPTCHA
  const [captcha, setCaptcha] = useState(() => {
    const num1 = Math.floor(Math.random() * 10);
    const num2 = Math.floor(Math.random() * 10);
    return { num1, num2, answer: num1 + num2 };
  });

  const {
    loading,
    success,
    message: contactMessage,
    error,
  } = useSelector((state) => state.contact);

  useSEO(BUY_ONLINE_ROUTE);

  const refreshCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10);
    const num2 = Math.floor(Math.random() * 10);
    setCaptcha({ num1, num2, answer: num1 + num2 });
    setCaptchaValue("");
    setCaptchaError("");
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      mobile: "",
      subject: "",
      message: "",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required("Name is required")
        .max(255, "Name must be at most 255 characters"),
      email: Yup.string()
        .required("Email is required")
        .email("Invalid email address"),
      mobile: Yup.string()
        .required("Mobile number is required")
        .matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits"),
      subject: Yup.string()
        .required("Subject is required")
        .max(255, "Subject must be at most 255 characters"),
      message: Yup.string()
        .required("Message is required")
        .max(255, "Message must be at most 255 characters"),
    }),
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      // Verify CAPTCHA
      if (parseInt(captchaValue) !== captcha.answer) {
        setCaptchaError("Invalid CAPTCHA. Please try again.");
        refreshCaptcha();
        setSubmitting(false);
        return;
      }

      setCaptchaError("");

      const contactData = {
        name: values.name,
        email: values.email,
        mobile: values.mobile,
        subject: values.subject,
        message: `Purchase Request - ${values.subject}: ${values.message}`,
      };

      const result = await dispatch(submitContactForm(contactData));

      if (submitContactForm.fulfilled.match(result)) {
        if (success) {
          toast.success("Your request has been submitted successfully!");
        } else if (error) {
          toast.error(String(error));
        }
        resetForm();
        setCaptchaValue("");
        refreshCaptcha();
        setTimeout(() => {
          dispatch(resetContactState());
        }, 5000);
      }

      setSubmitting(false);
    },
  });

  return (
    <Layout>
      <div className="mx-auto overflow-x-hidden max-w-7xl">
        <RectangleGridButtons isDarkMode={isDarkMode} />
      </div>
      <div
        className={`min-h-screen py-12 md:py-16 ${
          isDarkMode ? "bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800" : "bg-gradient-to-br from-gray-50 via-white to-gray-100"
        }`}
      >
        <div className="max-w-4xl px-4 mx-auto">
          {/* Header Section */}
          <div className="mb-10 text-center">
            <div
              className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-4 shadow-lg ${
                isDarkMode 
                  ? "bg-gradient-to-br from-green-600 to-green-700" 
                  : "bg-gradient-to-br from-green-500 to-green-600"
              }`}
            >
              <FiShoppingBag className="text-white w-9 h-9" />
            </div>
            <h1
              className={`text-3xl md:text-4xl font-bold mb-3 ${
                isDarkMode ? "text-white" : "text-gray-800"
              }`}
            >
              Request Your Product
            </h1>
            <p
              className={`text-base max-w-lg mx-auto ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Tell us what you're looking for, and we'll help you find the best deal
            </p>
          </div>

          {/* Form Card */}
          <div
            className={`rounded-2xl shadow-xl overflow-hidden transition-all duration-300 ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <div className="p-6 md:p-8">
              <form onSubmit={formik.handleSubmit} className="space-y-5">
                {/* Name Field */}
                <div className="group">
                  <label
                    className={`block text-sm font-semibold mb-2 transition-all ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <FiUser className="inline w-4 h-4 mr-2" /> 
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`w-full px-4 py-3 text-sm rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                      formik.touched.name && formik.errors.name
                        ? "border-red-500 focus:ring-red-500"
                        : isDarkMode
                          ? "border-gray-600 bg-gray-700 text-white focus:border-green-500 focus:ring-green-500"
                          : "border-gray-200 bg-gray-50 text-gray-900 focus:border-green-500 focus:ring-green-500"
                    }`}
                    placeholder="Enter your full name"
                  />
                  {formik.touched.name && formik.errors.name && (
                    <div className="flex items-center gap-1 mt-1 text-xs text-red-500 animate-fadeIn">
                      <FiAlertCircle className="w-3 h-3" />
                      {formik.errors.name}
                    </div>
                  )}
                </div>

                {/* Email Field */}
                <div className="group">
                  <label
                    className={`block text-sm font-semibold mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <FiMail className="inline w-4 h-4 mr-2" /> 
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`w-full px-4 py-3 text-sm rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                      formik.touched.email && formik.errors.email
                        ? "border-red-500 focus:ring-red-500"
                        : isDarkMode
                          ? "border-gray-600 bg-gray-700 text-white focus:border-green-500 focus:ring-green-500"
                          : "border-gray-200 bg-gray-50 text-gray-900 focus:border-green-500 focus:ring-green-500"
                    }`}
                    placeholder="you@example.com"
                  />
                  {formik.touched.email && formik.errors.email && (
                    <div className="flex items-center gap-1 mt-1 text-xs text-red-500">
                      <FiAlertCircle className="w-3 h-3" />
                      {formik.errors.email}
                    </div>
                  )}
                </div>

                {/* Mobile Field */}
                <div className="group">
                  <label
                    className={`block text-sm font-semibold mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <FiPhone className="inline w-4 h-4 mr-2" /> 
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="mobile"
                    value={formik.values.mobile}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    maxLength={10}
                    className={`w-full px-4 py-3 text-sm rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                      formik.touched.mobile && formik.errors.mobile
                        ? "border-red-500 focus:ring-red-500"
                        : isDarkMode
                          ? "border-gray-600 bg-gray-700 text-white focus:border-green-500 focus:ring-green-500"
                          : "border-gray-200 bg-gray-50 text-gray-900 focus:border-green-500 focus:ring-green-500"
                    }`}
                    placeholder="9876543210"
                  />
                  {formik.touched.mobile && formik.errors.mobile && (
                    <div className="flex items-center gap-1 mt-1 text-xs text-red-500">
                      <FiAlertCircle className="w-3 h-3" />
                      {formik.errors.mobile}
                    </div>
                  )}
                </div>

                {/* Subject Field */}
                <div className="group">
                  <label
                    className={`block text-sm font-semibold mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Product / Subject <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formik.values.subject}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`w-full px-4 py-3 text-sm rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                      formik.touched.subject && formik.errors.subject
                        ? "border-red-500 focus:ring-red-500"
                        : isDarkMode
                          ? "border-gray-600 bg-gray-700 text-white focus:border-green-500 focus:ring-green-500"
                          : "border-gray-200 bg-gray-50 text-gray-900 focus:border-green-500 focus:ring-green-500"
                    }`}
                    placeholder="e.g., iPhone 14 Pro, MacBook Air, etc."
                  />
                  {formik.touched.subject && formik.errors.subject && (
                    <div className="flex items-center gap-1 mt-1 text-xs text-red-500">
                      <FiAlertCircle className="w-3 h-3" />
                      {formik.errors.subject}
                    </div>
                  )}
                </div>

                {/* Message Field */}
                <div className="group">
                  <label
                    className={`block text-sm font-semibold mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <FiMessageSquare className="inline w-4 h-4 mr-2" /> 
                    Details <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    value={formik.values.message}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`w-full px-4 py-3 text-sm rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50 resize-none ${
                      formik.touched.message && formik.errors.message
                        ? "border-red-500 focus:ring-red-500"
                        : isDarkMode
                          ? "border-gray-600 bg-gray-700 text-white focus:border-green-500 focus:ring-green-500"
                          : "border-gray-200 bg-gray-50 text-gray-900 focus:border-green-500 focus:ring-green-500"
                    }`}
                    placeholder="Tell us your budget, preferred condition, specifications, or any special requirements..."
                  />
                  {formik.touched.message && formik.errors.message && (
                    <div className="flex items-center gap-1 mt-1 text-xs text-red-500">
                      <FiAlertCircle className="w-3 h-3" />
                      {formik.errors.message}
                    </div>
                  )}
                </div>

                {/* CAPTCHA */}
                <div>
                  <label
                    className={`block text-sm font-semibold mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <FiShieldOff className="inline w-4 h-4 mr-2" /> 
                    Verification <span className="text-red-500">*</span>
                  </label>
                  <div
                    className={`flex items-center gap-3 p-3 rounded-xl ${
                      isDarkMode ? "bg-gray-700" : "bg-gray-50"
                    }`}
                  >
                    <div
                      className={`text-lg font-bold px-5 py-2 rounded-lg ${
                        isDarkMode 
                          ? "bg-gray-600 text-white" 
                          : "bg-white border-2 border-gray-200 text-gray-800"
                      }`}
                    >
                      {captcha.num1} + {captcha.num2} = ?
                    </div>
                    <input
                      type="text"
                      value={captchaValue}
                      onChange={(e) => {
                        setCaptchaValue(e.target.value);
                        setCaptchaError("");
                      }}
                      className={`flex-1 px-3 py-2 text-sm rounded-lg border-2 focus:outline-none focus:ring-2 ${
                        captchaError
                          ? "border-red-500 focus:ring-red-500"
                          : isDarkMode
                            ? "border-gray-600 bg-gray-600 text-white focus:border-green-500 focus:ring-green-500"
                            : "border-gray-200 bg-white text-gray-900 focus:border-green-500 focus:ring-green-500"
                      }`}
                      placeholder="Enter answer"
                    />
                    <button
                      type="button"
                      onClick={refreshCaptcha}
                      className={`p-2 rounded-lg transition-all duration-200 ${
                        isDarkMode 
                          ? "bg-gray-600 hover:bg-gray-500" 
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      <FiAlertCircle className="w-4 h-4" />
                    </button>
                  </div>
                  {captchaError && (
                    <div className="flex items-center gap-1 mt-1 text-xs text-red-500">
                      <FiAlertCircle className="w-3 h-3" />
                      {captchaError}
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={formik.isSubmitting || loading}
                  className={`w-full py-3.5 text-sm font-bold rounded-xl transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 ${
                    formik.isSubmitting || loading
                      ? "opacity-50 cursor-not-allowed"
                      : isDarkMode
                        ? "bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white shadow-lg"
                        : "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg"
                  }`}
                >
                  {formik.isSubmitting || loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white rounded-full border-t-transparent animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <FiSend className="w-4 h-4" />
                      Submit Request
                    </>
                  )}
                </button>

                {/* Success Message */}
                {success && (
                  <div className="flex items-center justify-center gap-2 p-3 text-green-700 border border-green-200 rounded-lg bg-green-50 animate-fadeIn">
                    <FiCheckCircle className="w-4 h-4" />
                    <span className="text-sm">Request submitted successfully!</span>
                  </div>
                )}

                {/* Footer Note */}
                <p
                  className={`text-center text-xs pt-2 ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  We'll respond within 24 hours
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </Layout>
  );
};

export default BuyOnline;